import { situationCardSlotOrder, situationSlotProse, type SituationCardSlot } from "@peer-start/platform-contracts";

export type SituationCardSection = SituationCardSlot;
export type SituationCardCopy = Record<SituationCardSection, string>;

/** Drops generated source notes such as "gebaseerd op …". Those belong in the source list. */
function authoredParagraphs(text: string): string[] {
  return text
    .replace(/^[^\n—]+—\s*/, "")
    .split(/\n\s*\n/)
    .map((part) => part.trim())
    .filter((part) => part && !/^gebaseerd op\b/i.test(part));
}

function slotText(slots: ReadonlyArray<{ key: string; text: string | null }>, key: SituationCardSection): string {
  const raw = slots.find((slot) => slot.key === key)?.text;
  if (!raw) return "";
  const authored = authoredParagraphs(raw);
  if (authored.length > 0) return authored.join("\n\n");
  // Signals stays out until generation writes it, so the usual card matches the example.
  if (key === "signals") return "";
  return situationSlotProse[key];
}

/** Shows the Dutch wording of the shared example when the interface language is Dutch. */
export function localizeSlotText(
  key: SituationCardSection,
  text: string,
  translate: (key: string) => string,
): string {
  if (text !== situationSlotProse[key]) return text;
  return translate(`administration.situationCard.prose.${key}`);
}

/** Source numbers follow the order of the card's source list. */
export function situationSourceReferences(
  sources: readonly { id: string }[],
  slots: readonly { key: string; sourceIds: readonly string[] }[],
): Partial<Record<SituationCardSection, number[]>> {
  const numbers = new Map(sources.map((source, index) => [source.id, index + 1]));
  const references: Partial<Record<SituationCardSection, number[]>> = {};
  for (const key of situationCardSlotOrder) {
    const ids = slots.find((slot) => slot.key === key)?.sourceIds ?? [];
    const cited = [
      ...new Set(
        ids.flatMap((id) => {
          const number = numbers.get(id);
          return number === undefined ? [] : [number];
        }),
      ),
    ].sort((left, right) => left - right);
    if (cited.length > 0) references[key] = cited;
  }
  return references;
}

export type SourceFit = "fits" | "partial" | "unused";

export interface SourceContribution {
  level: SourceFit;
  sections: SituationCardSection[];
}

function paragraphCount(text: string): number {
  return text.split(/\n\s*\n/).map((part) => part.trim()).filter(Boolean).length;
}

/** Fit follows the visible paragraphs. A source that is not on the card yet stays unlabeled until generation. */
export function sourceContribution(
  sourceId: string,
  slots: ReadonlyArray<{ key: string; text: string | null; sourceIds: readonly string[] }>,
  sourcesChanged: boolean,
): SourceContribution | null {
  const assigned = slots.some((slot) => slot.sourceIds.includes(sourceId));
  if (!assigned && sourcesChanged) return null;
  const sections: SituationCardSection[] = [];
  let used = 0;
  let total = 0;
  for (const key of situationCardSlotOrder) {
    const count = paragraphCount(slotText(slots, key));
    if (count === 0) continue;
    total += count;
    if (slots.find((slot) => slot.key === key)?.sourceIds.includes(sourceId)) {
      used += count;
      sections.push(key);
    }
  }
  if (total === 0 || used === 0) return { level: "unused", sections: [] };
  return { level: used / total >= 0.5 ? "fits" : "partial", sections };
}

/** Sources that were assessed and do not appear in the card stay out of the source list. */
export function partitionCardSources<T extends { id: string }>(
  sources: readonly T[],
  slots: ReadonlyArray<{ key: string; text: string | null; sourceIds: readonly string[] }>,
  sourcesChanged: boolean,
): { applied: T[]; unapplied: T[] } {
  const applied: T[] = [];
  const unapplied: T[] = [];
  for (const source of sources) {
    if (sourceContribution(source.id, slots, sourcesChanged)?.level === "unused") unapplied.push(source);
    else applied.push(source);
  }
  return { applied, unapplied };
}

export function situationCardCopy(
  _title: string,
  _summary: string,
  slots: ReadonlyArray<{ key: string; text: string | null }>,
): SituationCardCopy {
  return Object.fromEntries(situationCardSlotOrder.map((key) => [key, slotText(slots, key)])) as SituationCardCopy;
}

export interface TextChange {
  kind: "same" | "added" | "removed";
  value: string;
}

/** Paragraph diff. A changed sentence stays intact instead of mixing old and new words. */
export function diffText(before: string, after: string): TextChange[] {
  const left = before.split(/\n\s*\n/).map((part) => part.trim()).filter(Boolean);
  const right = after.split(/\n\s*\n/).map((part) => part.trim()).filter(Boolean);
  const columns = right.length + 1;
  const lengths = new Uint16Array((left.length + 1) * columns);
  const at = (row: number, column: number) => row * columns + column;
  for (let row = 1; row <= left.length; row += 1) {
    for (let column = 1; column <= right.length; column += 1) {
      lengths[at(row, column)] =
        left[row - 1] === right[column - 1]
          ? lengths[at(row - 1, column - 1)]! + 1
          : Math.max(lengths[at(row - 1, column)]!, lengths[at(row, column - 1)]!);
    }
  }
  const stack: TextChange[] = [];
  let row = left.length;
  let column = right.length;
  while (row > 0 || column > 0) {
    if (row > 0 && column > 0 && left[row - 1] === right[column - 1]) {
      stack.push({ kind: "same", value: left[row - 1]! });
      row -= 1;
      column -= 1;
    } else if (column > 0 && (row === 0 || lengths[at(row, column - 1)]! >= lengths[at(row - 1, column)]!)) {
      stack.push({ kind: "added", value: right[column - 1]! });
      column -= 1;
    } else {
      stack.push({ kind: "removed", value: left[row - 1]! });
      row -= 1;
    }
  }
  return stack.reverse();
}

export interface VisibleChange {
  kind: "same" | "added" | "removed" | "rewritten";
  value: string;
}

/** A replaced paragraph is one rewrite. A paragraph that only appears or disappears stays added or removed. */
export function visibleChanges(before: string, after: string): VisibleChange[] {
  const parts = diffText(before, after);
  const changes: VisibleChange[] = [];
  for (let index = 0; index < parts.length; index += 1) {
    const part = parts[index]!;
    const next = parts[index + 1];
    if (part.kind === "removed" && next?.kind === "added") {
      changes.push({ kind: "rewritten", value: next.value });
      index += 1;
    } else {
      changes.push(part);
    }
  }
  return changes;
}

export interface NumberedStep {
  number: string;
  title: string;
  body: string;
}

/** Turns "1. Titel\\nTekst" blocks into steps. Other text stays a plain paragraph. */
export function parseNumberedSteps(text: string): NumberedStep[] | null {
  const matches = [
    ...text.matchAll(/(?:^|\n)(\d+)\.\s+([^\n]+)(?:\n+(?!\d+\.\s)([\s\S]*?))?(?=\n+\d+\.\s+|$)/g),
  ];
  if (matches.length === 0) return null;
  return matches.map((match) => ({
    number: match[1] ?? "",
    title: (match[2] ?? "").trim(),
    body: (match[3] ?? "").trim(),
  }));
}
